import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import LogRocket from 'logrocket';
import moment from 'moment';
import { Helmet } from 'react-helmet';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import mixpanel from 'mixpanel-browser';

import { Modal, Grid, Typography, IconButton } from '@material-ui/core';
import { OTAListing, Listing } from '../../../interfaces/idashboard';
import { Container } from '@mui/material';
import { DashboardState } from '../../../interfaces/idashboard';
import {
  DashboardPropTypes,
  CompsetFilterVal,
  FilterVal,
  VisibleListingsProp,
  ListingsProp,
} from '../../../interfaces/iproptypes';
import { makeStyles, Theme, createStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import {
  PricingDashboard,
  GuestsDashboard,
  SettingsDashboard,
  EstimatorDashboard,
  MyAnalysisDashboard,
  DiscoveryDashboard,
} from './Pages';
import {
  getFilteredListings,
  getVisibleListings,
  getVisibleOTAListings,
  isDefaultFilter,
  titleCase,
  makeLowerCase,
  extractVisibleListings,
  getBoundedListings,
  getBoundedOTAListings,
  extractVisibleOTAListings,
  getMarketingTrackers,
  getUniqueUserId,
  extractStaticListings,
  isEmpty,
  clearApiCache,
} from 'src/utils/functions';
import { useHighlight } from 'src/utils/hooks';
import { OnboardingModal, OnboardingModalMobile, SubscriptionModal, AuthModal } from '../../Modals';
import { Header, Sidebar, Filter } from '../../HOC';
import '../Dashboard.scss';
import {
  COLOR_PRIMARY,
  COLOR_PRIMARY_DARK,
  COLOR_PRIMARY_LIGHT,
  LIGHT_GREY,
  DARK_GREY,
  BACKGROUND_COLOR,
  DYNAMIC_PRICING_ANALYTICS_MIXPANEL_TOKEN,
  sampleMarket,
  sidebarWidth,
} from 'src/const';
import { callApi } from 'src/core/callApi';
import { getDistrictInfo, setUserCountryCode } from 'src/store/Dashboard/actions';
import EmailVerifyModal from 'src/App/Modals/EmailVerifyModal';
import { DynamicPricingDashboard } from '../Pricing';
import postOnSlack from 'src/core/postOnSlack';
import Asynchronous from 'src/App/HOC/Header/Ribbons/Asynchronous';
import { HomePage } from 'src/App/Pages';
import { timeZoneCCMap } from '../../assets/timeZoneCCMap';
import GranularDataDashboard from './Pages/GranularDataDashboard';
import { AirbticsProDashboard } from './Pages/AirbticsPro';
import AppTour from './Pages/AppTour';

import investmentLandingPage from 'src/App/assets/house-bg-home.jpg';
import IndividualOnBoardingPage from 'src/App/Pages/IndividualOnBoardingPage';
import { OverviewPage } from 'src/App/Pages/OverviewPage';
import ViewDemoButton from 'src/App/Components/Buttons/ViewDemoButton';

mixpanel.init('0c9d3451de8e0a1a2f21ec23b05c2063');
mixpanel.init(DYNAMIC_PRICING_ANALYTICS_MIXPANEL_TOKEN, {}, 'dynamic_pricing_analytics');

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      background: BACKGROUND_COLOR,
      boxShadow: `inset 0px 5px 10px 0px ${DARK_GREY}`,
      padding: 0,
      overflow: 'hidden',
    },
    content: {
      maxWidth: 4600,
      padding: 0,
      [theme.breakpoints.up(900)]: {
        marginLeft: sidebarWidth,
        transition: theme.transitions.create('padding', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
      },
    },
    contentShift: {
      [theme.breakpoints.up(900)]: {
        marginLeft: theme.spacing(8),
        transition: theme.transitions.create('padding', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },
    container: {
      [theme.breakpoints.down(1160)]: {
        paddingTop: theme.spacing(12),
      },
    },
    paperModal: {
      position: 'absolute',
      backgroundColor: BACKGROUND_COLOR,
      padding: theme.spacing(8),
      borderRadius: '70px',
      [theme.breakpoints.up('xs')]: {
        width: 340,
      },
      [theme.breakpoints.up('sm')]: {
        width: 530,
      },
      [theme.breakpoints.up('lg')]: {
        width: 650,
      },
    },
    signupModal: {
      position: 'absolute',
      [theme.breakpoints.up('xs')]: {
        width: 340,
      },
      [theme.breakpoints.up('sm')]: {
        width: 530,
      },
      [theme.breakpoints.up('lg')]: {
        width: 650,
      },
      height: 620,
      backgroundColor: LIGHT_GREY,
      borderRadius: 10,
      padding: theme.spacing(2, 4, 3),
    },
    warningModal: {
      position: 'absolute',
      backgroundColor: BACKGROUND_COLOR,
      padding: theme.spacing(4),
      borderRadius: 10,
      [theme.breakpoints.up('xs')]: {
        width: 300,
      },
    },
    customButton: {
      background: COLOR_PRIMARY_DARK,
      borderRadius: 5,
      border: 70,
      color: 'white',
      height: 45,
      padding: '0 15px',
      boxShadow: '0 1px 1px 1px rgba(255, 255, 255, .3)',
      '&:hover': {
        background: COLOR_PRIMARY_LIGHT,
      },
    },
    customButtonReversed: {
      background: BACKGROUND_COLOR,
      borderRadius: 5,
      border: 70,
      color: COLOR_PRIMARY_DARK,
      height: 45,
      padding: '0 15px',
      boxShadow: '0 1px 1px 1px rgba(255, 255, 255, .3)',
      '&:hover': {
        background: COLOR_PRIMARY_LIGHT,
      },
    },
    creditsText: {
      color: COLOR_PRIMARY,
      fontSize: '1rem',
      cursor: 'pointer',
    },
    iconButton: {
      padding: 3,
    },
    addCreditsBtn: {
      fontSize: 20,
      color: COLOR_PRIMARY_DARK,
      display: 'inline-block',
      marginBottom: '3px',
    },
    SEOLinkStyle: {
      position: 'relative',
      top: -500,
    },
  })
);

// consts
const setMarketingTrackingURL =
  'https://4b5jgzfm05.execute-api.us-east-2.amazonaws.com/default/setMarketingTracking';

const getEstimatorInvestmentCalculatorInputQueries = (searchParams: string) => {
  const propertyPriceQuery = new URLSearchParams(searchParams).get('propertyPrice') || '';
  const managementFeeQuery = new URLSearchParams(searchParams).get('managementFee') || '';
  const propertyTaxQuery = new URLSearchParams(searchParams).get('propertyTax') || '';
  const lodgingTaxQuery = new URLSearchParams(searchParams).get('lodgingTax') || '';
  const mortgageFeesQuery = new URLSearchParams(searchParams).get('mortgageFees') || '';
  const hoaFeesQuery = new URLSearchParams(searchParams).get('hoaFees') || '';
  const maintenanceQuery = new URLSearchParams(searchParams).get('maintenance') || '';
  const insuranceQuery = new URLSearchParams(searchParams).get('insurance') || '';
  const electricityQuery = new URLSearchParams(searchParams).get('electricity') || '';
  const wifiQuery = new URLSearchParams(searchParams).get('wifi') || '';
  const gasQuery = new URLSearchParams(searchParams).get('gas') || '';
  const otherQuery = new URLSearchParams(searchParams).get('other') || '';
  const homeFurnishingsQuery = new URLSearchParams(searchParams).get('homeFurnishings') || '';
  const closingCostsQuery = new URLSearchParams(searchParams).get('closingCosts') || '';
  const currencyQuery = new URLSearchParams(searchParams).get('currency') || '';

  return {
    propertyPriceQuery,
    managementFeeQuery,
    propertyTaxQuery,
    lodgingTaxQuery,
    mortgageFeesQuery,
    hoaFeesQuery,
    maintenanceQuery,
    insuranceQuery,
    electricityQuery,
    wifiQuery,
    gasQuery,
    otherQuery,
    homeFurnishingsQuery,
    closingCostsQuery,
    currencyQuery,
  };
};

const MarketDashboard = ({
  setListings,
  getMarkers,
  getGraphData,
  listings,
  otaListings,
  setOTAListings,
  multipleMarkersCallLoading,
  multipleOTAMarkersCallLoading,
  tailoredRegionMarkers,
  graph,
  reviewStream,
  bookingChanges,
  filteredBookings,
  search,
  compsets,
  compset,
  compsetListingIDs,
  user,
  currency,
  searchLimitAlertBox,
  getTokenThunk,
  getUserInfo,
  getBookingChanges,
  getTailoredRegionMarkers,
  getOTAMarkers,
  compsetCRUD,
  getCompsetListingIdsLoadStart,
  setCompsetListingIds,
  isUserAllowed,
  setCurrency,
  setSearchLimitAlertBox,
  bookingData,
  getBookingData,
  multipleBookingMarkersCallLoading,
  nearbyRecommendedMarkets,
  revPotentialRangeInvestment,
  priceTiers,
  getHighLowRevPotentialInvestment,
  googleAutocompleteSessionToken,
  getGoogleAutocompleteSessionToken,
  clearGoogleAutocompleteSessionToken,
  googleAutocompleteSessionTokenTimeStamp,
  boundsMapInsights,
  setBoundsMapInsights,
  filtersInsights,
  setFiltersInsights,
  setFilterInsightsActive,
  userCountryCode,
  setIndividualRevenuePotentialRange,
  getCachedSearchMarkets,
  cachedSearchMarkets,
  cachedMarketData,
  filteredListingsDashboard,
}: DashboardPropTypes) => {
  const classes = useStyles();
  const isLoggedIn = Boolean(getTokenThunk());
  const location = useLocation();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down(900));
  const isMobile = useMediaQuery(theme.breakpoints.down(520));
  const dispatch = useDispatch();

  const history = useHistory();
  const [appTourStep, setAppTourStep] = useState(0);
  const [showAppTour, setShowAppTour] = useState<boolean>(false);

  const [overviewDashboardLayoutView, setOverviewDashboardLayoutView] = useState<
    'dashboard' | 'map' | 'discovery' | 'compare'
  >('dashboard');

  const [topPerformingMarketOnFocus, setTopPerformingMarketOnFocus] = useState<any | null>(null);

  // State Variable for Estimator
  const [centerCoord, setCenterCoord] = useState<any>({ lat: 37.4209955, lng: -122.2071039 }); // default to Menlo Park
  const [address, setAddress] = useState<string>('');

  const [dynamicVisibleListings, setDynamicVisibleListings] = useState<VisibleListingsProp>({
    loading: false,
    data: [] as Listing[],
  });

  const appVisitCount = window.localStorage.getItem('appVisitCount');
  const pathname = window.location.pathname;
  const updateAppTourStep = step => {
    setAppTourStep(step);
  };

  const updateShowAppTour = val => {
    setShowAppTour(val);
  };

  useEffect(() => {
    if (
      (appVisitCount === null || Number(appVisitCount) <= 6) &&
      (pathname === '/airbnb-data' || pathname === '/airbnb-data/') &&
      isLoggedIn
    ) {
      setShowAppTour(true);
      setAppTourStep(0);
    }
    if (!appVisitCount) {
      window.localStorage.setItem('appVisitCount', String(1));
    } else {
      window.localStorage.setItem('appVisitCount', String(Number(appVisitCount) + 1));
    }
  }, []);

  useEffect(() => {
    let tailorRegionName = window.localStorage.getItem('tailoredRegion');

    if (tailorRegionName && isLoggedIn) {
      history.push(`/airbnb-data/0/0/${tailorRegionName}`);
      window.location.reload();
      localStorage.removeItem('tailoredRegion');
    }
  }, []);

  useEffect(() => {
    if (appTourStep === 0 && showAppTour) {
      window.scrollTo({
        top: 50,
        behavior: 'smooth',
      });
    }

    if (appTourStep == 1) {
      if (
        pathname == '/discovery' ||
        pathname == '/discovery/' ||
        pathname == '/airbnb-data/' ||
        pathname == '/airbnb-data'
      ) {
        let desiredState = {
          showInsightsDashboard: true,
          showEstimatorDashboard: false,
          showMyAnalysisDashboard: false,
          showSettingsDashboard: false,
          showDynamicPricingDashboard: false,
          showTailoredRegion: false,
        };
        setdashboardState(desiredState);
      }
      if (
        (pathname === '/airbnb-data/' || pathname === '/airbnb-data') &&
        redirectSearchPageQuery !== 'true'
      ) {
        const redirectTo = '/airbnb-data/united-kingdom/0/the%20city%20of%20brighton%20and%20hove';
        window.history.pushState({}, '', redirectTo);
        dispatch(getDistrictInfo('the city of brighton and hove', '0', 'united-kingdom'));
      }

      setTimeout(() => {
        window.scrollTo({
          top: window.outerHeight,
          behavior: 'smooth',
        });
      }, 500);
    }

    if (appTourStep === 1) {
      window.scrollTo({
        top: window.outerHeight,
        behavior: 'smooth',
      });
    }

    if (appTourStep === 2) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }

    if (appTourStep === 3) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }

    if (appTourStep == 6) {
      setSubscriptionModalOpen(true);
      dispatch(getDistrictInfo('the city of brighton and hove', '0', 'united-kingdom'));
    } else if (appTourStep == 4 || appTourStep == 3 || appTourStep == 2 || appTourStep == 1) {
      setSubscriptionModalOpen(false);
      const redirectTo = '/airbnb-data/united-kingdom/0/the%20city%20of%20brighton%20and%20hove';
      redirectSearchPageQuery !== 'true' && window.history.pushState({}, '', redirectTo);
    } else {
      setSubscriptionModalOpen(false);
    }
  }, [appTourStep]);

  useEffect(() => {
    if (!Intl || !Intl.DateTimeFormat) {
      console.log('Intl API is not available in this environment.');
      dispatch(setUserCountryCode('US'));
    }
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    console.log(
      'User Time Zone: ',
      userTimeZone,
      ' and Country Code: ',
      timeZoneCCMap[userTimeZone]
    );
    dispatch(setUserCountryCode(timeZoneCCMap[userTimeZone]));
  }, [Intl]);

  useEffect(() => {
    let userNameFromLocalstorage = window.localStorage.getItem('user-name');
    let userEmailFromLocalstorage = window.localStorage.getItem('user-email');

    if (user.email !== 'Trial' && user.email !== userEmailFromLocalstorage) {
      // Checking if the email from the localstorage and current emails are same or not
      //if not, updating the localstorage
      window.localStorage.setItem('user-email', user.email);
      userEmailFromLocalstorage = user.email;
    }

    if (user.userName !== 'Trial' && user.userName !== userNameFromLocalstorage) {
      // Checking if the name from the localstorage and current name are same or not
      //if not, updating the localstorage
      window.localStorage.setItem('user-name', user.userName);
      userNameFromLocalstorage = user.userName;
    }

    if ((window as any).Tawk_API) {
      (window as any).Tawk_API.onLoad = () => {
        (window as any).Tawk_API.setAttributes(
          {
            name: userNameFromLocalstorage,
            email: userEmailFromLocalstorage,
          },
          error => {
            console.error('Tawk API Error:', error);
          }
        );
        (window as any).Tawk_API.hideWidget();
      };
    }
  }, [user.email]);

  // url params
  let { country, city, district, isTailored, settings } = useParams<{
    country: string;
    city: string;
    district: string;
    isTailored: string;
    settings: string;
  }>();

  // query params
  const searchParams = location.search;
  const langQuery = new URLSearchParams(searchParams).get('lang') || '';
  const pageQuery = new URLSearchParams(searchParams).get('page') || '';
  const typeQuery = new URLSearchParams(searchParams).get('type') || '';
  const regionQuery = new URLSearchParams(searchParams).get('region') || '';
  const redirectSearchPageQuery = new URLSearchParams(searchParams).get('searchPage') || '-1';
  const affiliatedByQuery = new URLSearchParams(searchParams).get('affiliatedBy') || '-1';

  const isFullPageSearchBarOpen =
    (pathname === '/airbnb-data' || pathname === '/airbnb-data/') &&
    redirectSearchPageQuery === 'true';
  const referrer = document.referrer;
  const [langTracker, pageTracker, typeTracker, regionTracker] = getMarketingTrackers();
  if (!langTracker && !pageTracker && !typeTracker && !regionTracker) {
    localStorage.setItem(
      'marketing-trackers',
      JSON.stringify({
        lang: langQuery,
        page: pageQuery ? pageQuery : referrer,
        type: typeQuery,
        region: regionQuery,
      })
    );
  }
  const estimatorCoordQuery = new URLSearchParams(searchParams).get('estimatorCoord') || '';
  const estimatorAddressQuery = new URLSearchParams(searchParams).get('estimatorAddress') || '';
  const estimatorBedroomsQuery = new URLSearchParams(searchParams).get('bedrooms') || '';
  const estimatorBathroomsQuery = new URLSearchParams(searchParams).get('bathrooms') || '';
  const estimatorInvestmentCalculatorInputQueries = getEstimatorInvestmentCalculatorInputQueries(
    searchParams
  );
  const paymentPlanQuery = new URLSearchParams(searchParams).get('payment-plan') || '';
  const paymentDurationQuery = new URLSearchParams(searchParams).get('payment-duration') || '';

  useEffect(() => {
    if (search.data && search.data.currency) {
      setCurrency(search.data.currency);
    }
  }, [search.data]);

  // declarations
  const [landing, setLanding] = useState('investing');
  const states = require('us-state-codes');
  const cityName =
    search.data.countryCode == 'US' || country == 'united-states'
      ? states.getStateNameByStateCode(city)
      : search.data.city;
  const countryParam = search.data.country
    ? makeLowerCase(search.data.country).replace(/ /g, '-')
    : '0';
  const cityParam = search.data.city ? makeLowerCase(search.data.city) : '0';
  const districtParam = makeLowerCase(search.data.name);
  const [isFilterChange, setIsFilterChange] = useState(true);
  const [sample, setSample] = useState(true);
  const [dynamicTrigger, setDynamicTrigger] = useState(false);
  const [filterVal, setFilterVal] = useState({
    roomType: ['all'],
    bedrooms: [100],
    bathrooms: [100],
    revenueFilter: ['all'],
    priceTierFilter: ['all'],
    accommodates: [1, 16],
    minimum_nights: [1, 30],
    number_of_reviews: 'all',
    rating: [3.9, 5],
    hostType: 'both',
    amenities: {
      pool: null,
      parking: null,
      hot_tub: null,
      gym: null,
      pets: null,
      dryer: null,
      washer: null,
      kitchen: null,
      ac: null,
      heating: null,
      wifi: null,
      tv: null,
      bbq_grill: null,
      fireplace: null,
      breakfast: null,
      beachfront: null,
      amazing_views: null,
    },
    showAllBookingComListings: false,
  } as FilterVal);
  const [filterActive, setFilterActive] = useState(false);
  const [dashboardState, setdashboardState] = React.useState({
    showInsightsDashboard: true,
    showEstimatorDashboard: false,
    showMyAnalysisDashboard: false,
    showSettingsDashboard: false,
    showDynamicPricingDashboard: false,
    showTailoredRegion: false,
  } as DashboardState);
  const isHomePage = window.location.pathname === '/' && !dashboardState.showMyAnalysisDashboard;

  const [selectedSidebarIndex, setSelectedSidebarIndex] = useState(
    window.location.pathname === '/' ? -1 : 0
  );
  let isAdmin = user && user.role && user.role === 'admin' ? true : false;
  const [openAlert, setOpenAlert] = React.useState(true);
  const [isTailoredRegionOpen, setIsTailoredRegionOpen] = React.useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [showSearchCreditDeductWarningModal, setShowSearchCreditDeductWarningModal] = useState(
    false
  );
  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  const [filterApplied, setFilterApplied] = useState(false);
  const [filterOpen, setFilterOpen] = React.useState(false);
  const [overviewDashboardMode, setOverviewDashboardMode] = useState({
    showInvesting: true,
    showRentalArbitrage: false,
  });
  const [isEmailVerifyModalOpen, setIsEmailVerifyModalOpen] = useState<boolean>(false);
  const [isUserVerified, setIsUserVerified] = useState<boolean>(true);
  const [rateLimitAlert, setRateLimitAlert] = useState<boolean>(false);

  const [filteredListings, setFilteredListings] = useState<string[] | null>(null);
  const [filteredOTAListings, setFilteredOTAListings] = useState<string[] | null>(null);

  // temp redirection to insights landing
  if (isHomePage) {
    // this below if condition is to keep track that user came from which affiliated partner
    if (affiliatedByQuery !== '-1') {
      localStorage.setItem('affiliatedKey', affiliatedByQuery);
    }
    window.open('/airbnb-data', '_self');
  }

  useEffect(() => {
    const fetchData = async () => {
      let appVisitCount = localStorage.getItem('appVisitCount');
      let affiliatedKey = localStorage.getItem('affiliatedKey');

      if (appVisitCount === '2') {
        const url =
          'https://ak27jez2w0.execute-api.us-east-2.amazonaws.com/default/affiliatePartnerStats';
        const config = {
          queryParams: {
            operationType: 'update',
            updatedKey: 'visitors_count',
            affiliateKey: affiliatedKey,
          },
        };

        if (affiliatedKey) {
          const res = await callApi(url, config, false);
          console.log('Response:', res);
        }
      }

      clearApiCache();
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (overviewDashboardLayoutView === 'map') {
      setSidebarOpen(false);
    }
  }, [overviewDashboardLayoutView]);

  useEffect(() => {
    if (user.isVerified != isUserVerified) {
      setIsUserVerified(user.isVerified);
    }
  }, [user, user.isVerified]);

  // This UseEffect is responsible to show Email verification modal when after user search for 3 markets or after 1hr of signup
  useEffect(() => {
    let totalSearchDoneByUser =
      Number(window.localStorage.getItem('investment-search-attempts')) || 0;
    if (isUserVerified === false && totalSearchDoneByUser >= 3) {
      setIsEmailVerifyModalOpen(true);
      setRateLimitAlert(true);
    }
    if (isUserVerified === false) {
      setTimeout(() => {
        setIsEmailVerifyModalOpen(true);
      }, 3600000); //1 hr
    }
  }, [isUserVerified]);

  // analytics

  useEffect(() => {
    getCachedSearchMarkets();
    if (pageQuery && !isLoggedIn) {
      callApi(
        setMarketingTrackingURL,
        {
          queryParams: {
            page: pageQuery,
          },
        },
        false
      );
    }
  }, []);

  useEffect(() => {
    let isReturningUser = false;
    const timeNow = moment();
    const lastVisit = localStorage.getItem('visit-time');
    if (lastVisit) {
      const lastVisitMoment = moment(lastVisit);
      const diff = timeNow.diff(lastVisitMoment, 'hours');
      isReturningUser = diff > 1;
    } else {
      localStorage.setItem('visit-time', timeNow.toISOString());
    }
    if (
      isLoggedIn &&
      user &&
      user.email &&
      user.userName != 'Trial' &&
      !user.email.includes('@airbtics.com')
    ) {
      LogRocket.init('zqebrc/airbtics');
      LogRocket.identify(user?.email as string, {
        name: user?.userName as string,
        email: user?.email as string,
        isPaid: user.regions ? true : false,
        isReturningUser: isReturningUser,
      });
    }

    if (user && user.email != 'Trial' && user.userName != 'Trial') {
      mixpanel.dynamic_pricing_analytics.identify(user.email);
      if (user.isSubscribed) {
        mixpanel.dynamic_pricing_analytics.people.set({
          $name: user.userName,
          $email: user.email,
          $plan: 'paid',
        });
      }
      mixpanel.dynamic_pricing_analytics.people.set({
        $name: user.userName,
        $email: user.email,
        $plan: 'unpaid',
      });

      const uniqueUserId: string | null = getUniqueUserId(user);

      try {
        console.log(
          'Unique User ID: ',
          uniqueUserId,
          ' | Is HotJar Active ? ',
          (window as any).hj ? true : false
        );
        (window as any).hj &&
          (window as any).hj('identify', uniqueUserId, {
            email: user.email,
          });
      } catch (err) {
        console.log('Error while setting up the user email in hotjar: ', err);
      }
    }
  }, [user]);

  //checking if the filter is applyed or not
  useEffect(() => {
    setFilterApplied(isDefaultFilter());
  }, []);

  // get district info
  useEffect(() => {
    if (country && city && district) {
      setSample(false);
      setIsFilterChange(false);
      dispatch(getDistrictInfo(district, city, country));
    }

    if (settings && settings === 'settings') {
      handleMarketSidebarClick('settings');
    }
  }, []);

  // login/signup modal popup logic

  useEffect(() => {
    if (
      !isLoggedIn &&
      !isHomePage &&
      !location.pathname.includes('airbnb-calculator') &&
      !location.pathname.includes('tailored/t')
    ) {
      setLoginModalOpen(true);
    }
  }, [isLoggedIn, dashboardState]);

  useEffect(() => {
    if (paymentPlanQuery || paymentDurationQuery) {
      if (isLoggedIn) {
        // setSubscriptionModalOpen(true);
      } else {
        setLoginModalOpen(true);
      }
    }
  }, [isLoggedIn]);

  // api calls
  useEffect(() => {
    getUserInfo();
  }, [getUserInfo]);

  // route checks
  useEffect(() => {
    if (window.location.pathname.includes('my-analysis')) {
      setLanding('my-analysis');
      handleMarketSidebarClick('myAnalysis');
      handleSidebarListItemClick(5);
    }
    if (location.pathname.includes('airbnb-calculator')) {
      setLanding('estimator');
      handleMarketSidebarClick('estimator');
      handleSidebarListItemClick(1);
    }
    if (location.pathname.includes('markets')) {
      setLanding('discovery');
      if (!district) {
        dispatch(getDistrictInfo('the city of brighton and hove', '0', 'united-kingdom'));
      }
      setdashboardState(prev => ({ ...prev, showInsightsDashboard: true }));
      setOverviewDashboardLayoutView('discovery');
    }
    if (location.pathname.includes('pricing')) {
      setLanding('dynamic-pricing');
      handleMarketSidebarClick('listings');
      handleSidebarListItemClick(7);
    }
    if (location.pathname.includes('settings')) {
      setLanding('settings');
      handleMarketSidebarClick('settings');
      handleSidebarListItemClick(10);
    }
    if (location.pathname.includes('/tailored/t')) {
      setLanding('tailoredRegion');
      handleMarketSidebarClick('tailoredRegion');
      handleSidebarListItemClick(11);
    }

    const paramsGuests = new URLSearchParams(window.location.search).get('guests');
    const url = window.location.toString().split('?')[0];
    if (paramsGuests === 'true') {
      handleMarketSidebarClick('guests');
      handleSidebarListItemClick(4);
      window.history.pushState({}, '', url);
    } else if (paramsGuests === 'fetching') {
      window.history.pushState({}, '', url);
    }
  }, [location, window.location.search]);

  const setTailorRegionLogic = (isTailored: string) => {
    if (isTailored) {
      setIsTailoredRegionOpen(true);
    }
  };

  useEffect(() => setTailorRegionLogic(isTailored));

  // dashboard logic
  useEffect(() => {
    setFiltersInsights(filterVal);
  }, [filterVal]);

  useEffect(() => {
    setFilterInsightsActive(filterActive);
  }, [filterActive]);

  const applyFilter = (
    revenueRange: { [key: number]: 'visible' | 'hidden' } | undefined = undefined,
    onlyConsiderRevenueFilter: boolean = false,
    filtersToConsider: string[] = []
  ) => {
    setDynamicTrigger(true);
    setTimeout(() => {
      setDynamicTrigger(false);
    }, 500);

    setFilterVal((e: FilterVal) => {
      if (!listings.loading && !otaListings.loading) {
        const [tempFilteredListings, tempFilteredOTAListings] = getFilteredListings(
          listings.data,
          otaListings.data,
          e,
          true,
          revenueRange,
          onlyConsiderRevenueFilter,
          filtersToConsider
        ) as [string[], string[]];

        setFilteredListings(tempFilteredListings);
        setFilteredOTAListings(tempFilteredOTAListings);

        const boundedListings =
          overviewDashboardLayoutView === 'dashboard'
            ? tempFilteredListings
            : getBoundedListings(listings.data, boundsMapInsights);
        const visibleListings = new Set(getVisibleListings(boundedListings, tempFilteredListings));

        let updatedListings = {};
        Object.keys(listings.data).forEach(listingID => {
          updatedListings[listingID] = {
            ...listings.data[listingID],
            visible: visibleListings.has(listingID) ? true : false,
          };
        });
        setListings(updatedListings);
        setDynamicVisibleListings(
          extractVisibleListings({ data: updatedListings, loading: false })
        );
        const boundedOTAListings = getBoundedOTAListings(otaListings.data, boundsMapInsights);
        const visibleOTAListings = new Set(
          getVisibleOTAListings(boundedOTAListings, tempFilteredOTAListings)
        );
        let updatedOTAListings = {};
        Object.keys(otaListings.data).forEach(listingID => {
          updatedOTAListings[listingID] = {
            ...otaListings.data[listingID],
            visible: visibleOTAListings.has(listingID) ? true : false,
          };
        });
        setOTAListings(updatedOTAListings);
      }
      mixpanel.track('applyFilter', {
        filters: e,
      });
      return e;
    });
  };

  const resetRevenuePotentialFilter = () => {
    const newRevPotentialRange = { ...revPotentialRangeInvestment };
    for (const key in newRevPotentialRange) {
      newRevPotentialRange[key] = 'visible';
    }
    setIndividualRevenuePotentialRange(newRevPotentialRange);
  };

  const resetFilter = () => {
    setDynamicTrigger(true);
    setTimeout(() => {
      setDynamicTrigger(false);
    }, 500);
    setFilterActive(false);
    if (!listings.loading && !otaListings.loading) {
      const filteredListings = Object.keys(listings.data);
      setFilteredListings(filteredListings);
      setFilteredOTAListings(Object.keys(otaListings.data));
      resetRevenuePotentialFilter();

      const boundedListings = getBoundedListings(listings.data, boundsMapInsights);
      const visibleListings = new Set(getVisibleListings(boundedListings, filteredListings));

      let updatedListings = {};
      Object.keys(listings.data).forEach(listingID => {
        updatedListings[listingID] = {
          ...listings.data[listingID],
          visible: visibleListings.has(listingID) ? true : false,
        };
      });
      setListings(updatedListings);
      setDynamicVisibleListings(extractVisibleListings({ data: updatedListings, loading: false }));

      setFilterVal(prevFilterVal => ({
        ...prevFilterVal,
        roomType: ['all'],
        bedrooms: [100],
        bathrooms: [100],
        revenueFilter: ['all'],
        priceTierFilter: ['all'],
        accommodates: [1, 16],
        minimum_nights: [1, 30],
        number_of_reviews: 'all',
        rating: [3.9, 5],
        amenities: {
          pool: null,
          parking: null,
          hot_tub: null,
          gym: null,
          pets: null,
          dryer: null,
          washer: null,
          kitchen: null,
          ac: null,
          heating: null,
          wifi: null,
          tv: null,
          bbq_grill: null,
          fireplace: null,
          breakfast: null,
          beachfront: null,
          amazing_views: null,
        },
        hostType: 'both',
      }));

      [
        'room_type',
        'bedroom_number',
        'bathroom_number',
        'no_of_people',
        'host_type',
        'amenities',
        'minimum_nights',
        'number_of_reviews',
        'rating',
      ].forEach(localStorageValue => {
        localStorage.removeItem(localStorageValue);
      });

      const boundedOTAListings = getBoundedOTAListings(otaListings.data, boundsMapInsights);
      const visibleOTAListings = new Set(getVisibleOTAListings(boundedOTAListings, []));
      let updatedOTAListings = {};
      Object.keys(otaListings.data).forEach(listingID => {
        updatedOTAListings[listingID] = {
          ...otaListings.data[listingID],
          visible: visibleOTAListings.has(listingID) ? true : false,
        };
      });
      setOTAListings(updatedOTAListings);
    }
  };

  const handleMarketSidebarClick = async (event: string) => {
    let emptyState = {
      showInsightsDashboard: false,
      showEstimatorDashboard: false,
      showMyAnalysisDashboard: false,
      showSettingsDashboard: false,
      showDynamicPricingDashboard: false,
      showTailoredRegion: false,
    };

    if (event === 'main') {
      emptyState['showInsightsDashboard'] = true;
    }
    if (event === 'estimator') {
      emptyState['showEstimatorDashboard'] = true;
    }
    if (event === 'myAnalysis') {
      emptyState['showMyAnalysisDashboard'] = true;
      window.history.pushState({}, '', '/my-analysis');
    }
    if (event === 'settings') {
      emptyState['showSettingsDashboard'] = true;
      window.history.pushState({}, 'Settings', '/user/settings');
    }
    if (event === 'tailoredRegion') {
      emptyState['showTailoredRegion'] = true;
      setIsTailoredRegionOpen(true);
    }
    if (event === 'listings') {
      emptyState['showDynamicPricingDashboard'] = true;
      const hasUserVisitedDynamicPricing =
        localStorage.getItem('visited-dynamic-pricing') === 'true';
      if (user && user.email && user.email != 'Trial' && !hasUserVisitedDynamicPricing) {
        try {
          const returnedData = await postOnSlack(
            'dynamic-pricing-monitor',
            `${user.email} checked out Dynamic Pricing.\n`
          );
          if (returnedData) {
            window.localStorage.setItem('visited-dynamic-pricing', 'true');
          }
        } catch (err) {
          console.log(err);
        }
      }
    }

    if (event === 'estimator') {
      window.history.pushState(
        {
          coordinate: estimatorCoordQuery,
          address: estimatorAddressQuery,
          bedrooms: estimatorBedroomsQuery,
          bathrooms: estimatorBathroomsQuery,
          estimatorInvestmentCalculatorInputs: estimatorInvestmentCalculatorInputQueries,
        },
        '',
        '/airbnb-calculator'
      );
    } else if (event === 'listings') {
      mixpanel.dynamic_pricing_analytics.register_once({
        'Shown Interest': new Date().toISOString(),
      });
      mixpanel.dynamic_pricing_analytics.track('clickedDynamicPricingTab');
      window.history.pushState({}, '', '/pricing');
    } else if (event === 'tailoredRegion') {
      window.history.pushState({}, 'TailoredRegion', '/tailored/t');
    } else if (event !== 'settings' && event !== 'myAnalysis' && event !== 'discovery') {
      window.history.pushState(
        {},
        '',
        countryParam == 'united-kingdom' &&
          cityParam == '0' &&
          districtParam == 'the city of brighton and hove' &&
          !emptyState['showGuestsDashboard']
          ? '/airbnb-data'
          : districtParam.trim() === ''
          ? '/airbnb-data'
          : `/airbnb-data/${countryParam}/${cityParam}/${districtParam}`
      );
    }
    setdashboardState(emptyState);
  };

  const handleSidebarListItemClick = (index: number) => {
    setShowAppTour(false);
    setSelectedSidebarIndex(index);
  };

  const toggleDrawer = () => {
    setFilterOpen(!filterOpen);
  };

  if (!country) {
    country = '';
  }

  // Onboarding
  const { isHighlight, highlightElement, removeHighlightElement } = useHighlight();

  const handleOnSampleClick = () => {
    const userCredits: number = user && user.credits ? user.credits : 0;
    if (!isLoggedIn) {
      setLoginModalOpen(true);
    } else if (userCredits > 0) {
      let page = window.location.pathname.split('/')[1];
      if (page === 'markets') {
        setSubscriptionModalOpen(true);
      } else {
        setShowSearchCreditDeductWarningModal(true);
      }
    } else {
      setSubscriptionModalOpen(true);
    }
  };

  const OnBoardingSearchBox = () => (
    <Asynchronous
      search={search}
      showAlert={() => {}}
      notShowAlert={() => {}}
      setdashboardState={setdashboardState}
      matches={false}
      user={user}
      district={district}
      isUserVerified={isUserVerified}
      rateLimitAlert={rateLimitAlert}
      setRateLimitAlert={setRateLimitAlert}
      googleAutocompleteSessionToken={googleAutocompleteSessionToken}
      getGoogleAutocompleteSessionToken={getGoogleAutocompleteSessionToken}
      clearGoogleAutocompleteSessionToken={clearGoogleAutocompleteSessionToken}
      googleAutocompleteSessionTokenTimeStamp={googleAutocompleteSessionTokenTimeStamp}
      isOnboardingOn={true}
      isMobileScreen={matches}
      cachedSearchMarkets={cachedSearchMarkets}
      appTourStep={appTourStep}
    />
  );

  const FullSearchPage = (
    <IndividualOnBoardingPage
      SearchBox={OnBoardingSearchBox}
      onSamplePageClick={() => {
        window.location.href =
          '/airbnb-data/united-kingdom/0/the%20city%20of%20brighton%20and%20hove';
      }}
      onVideoClick={() => {
        window.open('https://youtu.be/l46RvDRRF1E', 'nofollow');
      }}
      cover={investmentLandingPage}
    />
  );

  // SEO

  let SEODistrictName = isFilterChange ? search.data.name : district;
  let SEOVisibleListingsCount = listings.loading ? '' : Object.keys(listings).length;
  let SEOCountryName = isFilterChange ? search.data.country : country;
  let SEOCityName = cityName ? cityName : '';
  let SEOrecommendPageURL0 =
    'https://app.airbtics.com/airbnb-data/united-states/ca/san%20francisco';
  let SEOrecommendPageURL1 = 'https://app.airbtics.com/airbnb-data/united-states/fl/miami';
  let SEOrecommendPageDistrictName0 = 'San Francisco';
  let SEOrecommendPageDistrictName1 = 'Miami';
  try {
    if (search && search.data && search.data.recommendPageURLs) {
      SEOrecommendPageURL0 = search.data.recommendPageURLs[0];
      SEOrecommendPageDistrictName0 = SEOrecommendPageURL0.split('/').pop() || '';
      if (search.data.recommendPageURLs.length > 1) {
        SEOrecommendPageURL1 = search.data.recommendPageURLs[1];
        SEOrecommendPageDistrictName1 = SEOrecommendPageURL1.split('/').pop() || '';
      }
    }
  } catch (error) {
    console.log('Error while getting recommend page for SEO ', error);
  }

  const appBarHeight =
    ((theme.mixins.toolbar.minHeight ? theme.mixins.toolbar.minHeight : 0) as number) - 10;

  return (
    <>
      {/* title and meta tags */}
      <Helmet>
        <meta charSet="utf-8" />
        <title>
          {`Airbnb Data on ${SEOVisibleListingsCount} listings in ${titleCase(
            SEODistrictName
          )} | Airbtics | ${SEOCountryName}, ${SEOCityName}`}
        </title>
        <meta
          name="description"
          content={`Occupancy rate, daily rate, and monthly revenue data in ${SEODistrictName}. Here's all your short-term rental data to invest Airbnb smarter! `}
        />
      </Helmet>

      <h1
        className={classes.SEOLinkStyle}
        style={{
          marginTop: isMobile ? '-40px' : 0,
        }}
      >
        {`Analyze ${SEOVisibleListingsCount} short-term rentals in ${titleCase(
          SEODistrictName
        )}! You can find occupancy rate, daily rate, and annual revenue of rentals. `}
      </h1>
      <a
        href={SEOrecommendPageURL0}
        className={classes.SEOLinkStyle}
        style={{
          marginTop: isMobile ? '-40px' : 0,
        }}
      >
        {' '}
        {`See short-term rental data in ${SEOrecommendPageDistrictName0}`}{' '}
      </a>
      <a
        href={SEOrecommendPageURL1}
        className={classes.SEOLinkStyle}
        style={{
          marginTop: isMobile ? '-40px' : 0,
        }}
      >
        {' '}
        {`See Airbnb occupancy, daily rate and revenue data in ${SEOrecommendPageDistrictName1}`}{' '}
      </a>

      {rateLimitAlert && (
        <ViewDemoButton
          handleClick={() => setIsEmailVerifyModalOpen(true)}
          buttonText="You have used up all 3 free searches. Please verify your email to get free searches."
          topOffsetMobile="3%"
        />
      )}

      <Header
        setLoginModalOpen={setLoginModalOpen}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        filterOpen={filterOpen}
        toggleDrawer={toggleDrawer}
        dashboardState={dashboardState}
        setdashboardState={setdashboardState}
        matches={matches}
        filterApplied={filterApplied}
        handleMarketSidebarClick={handleMarketSidebarClick}
        setSubscriptionModalOpen={setSubscriptionModalOpen}
        district={district}
        isUserVerified={isUserVerified}
        rateLimitAlert={rateLimitAlert}
        setRateLimitAlert={setRateLimitAlert}
        googleAutocompleteSessionToken={googleAutocompleteSessionToken}
        getGoogleAutocompleteSessionToken={getGoogleAutocompleteSessionToken}
        clearGoogleAutocompleteSessionToken={clearGoogleAutocompleteSessionToken}
        googleAutocompleteSessionTokenTimeStamp={googleAutocompleteSessionTokenTimeStamp}
        cachedSearchMarkets={cachedSearchMarkets}
        appTourStep={appTourStep}
        updateShowAppTour={updateShowAppTour}
        handleSidebarListItemClick={handleSidebarListItemClick}
        centerCoord={centerCoord}
        setCenterCoord={setCenterCoord}
        address={address}
        setAddress={setAddress}
        dashboardLayoutView={overviewDashboardLayoutView}
      />

      <Sidebar
        parent="market"
        handleSidebarClick={handleMarketSidebarClick}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        matches={matches}
        selectedSidebarIndex={selectedSidebarIndex}
        handleSidebarListItemClick={handleSidebarListItemClick}
        currency={currency}
        setCurrency={setCurrency}
        isUserVerified={isUserVerified}
        setIsEmailVerifyModalOpen={setIsEmailVerifyModalOpen}
        showAppTour={showAppTour}
        dashboardState={dashboardState}
        setSubscriptionModalOpen={setSubscriptionModalOpen}
        user={user}
      />

      <Filter
        listings={extractVisibleListings(listings)}
        allListings={listings}
        isDisabled={!isUserAllowed}
        filterVal={filterVal}
        setFilterVal={setFilterVal}
        applyFilter={applyFilter}
        open={filterOpen}
        toggleDrawer={toggleDrawer}
        setFilterActive={setFilterActive}
        matches={matches}
        setFilterApplied={setFilterApplied}
        resetFilter={resetFilter}
        currency={currency}
        revPotentialRangeInvestment={revPotentialRangeInvestment}
        setIndividualRevenuePotentialRange={setIndividualRevenuePotentialRange}
        priceTiers={priceTiers}
        handleOnSampleClick={handleOnSampleClick}
        isListingLoading={!listings.data ? true : isEmpty(listings.data)}
      />

      {loginModalOpen && !isLoggedIn && (
        <AuthModal
          authModalOpen={loginModalOpen}
          setAuthModalOpen={setLoginModalOpen}
          getUserInfo={getUserInfo}
          getTokenThunk={getTokenThunk}
          matches={matches}
          landing={landing}
        />
      )}

      {subscriptionModalOpen && (
        <SubscriptionModal
          countryCode={search.data.countryCode}
          city={search.data.city}
          district={search.data.name}
          getTokenThunk={getTokenThunk}
          subscriptionModalOpen={subscriptionModalOpen}
          setSubscriptionModalOpen={setSubscriptionModalOpen}
          paymentPlanQuery={paymentPlanQuery}
          paymentDurationQuery={paymentDurationQuery}
          setIsTailoredRegionOpen={setIsTailoredRegionOpen}
          user={user}
        />
      )}

      {isFullPageSearchBarOpen && (
        <>
          <main
            className={clsx(classes.root, {
              [classes.content]: sidebarOpen,
              [classes.contentShift]: !sidebarOpen,
            })}
          >
            <Container
              className={classes.container}
              style={{
                paddingTop: appBarHeight,
                maxWidth: 4600,
                paddingLeft: 0,
                paddingRight: 0,
                paddingBottom: 0,
              }}
            >
              {FullSearchPage}
            </Container>
          </main>
        </>
      )}

      {!isHomePage && !isFullPageSearchBarOpen && (
        <>
          <main
            className={clsx(classes.root, {
              [classes.content]: sidebarOpen,
              [classes.contentShift]: !sidebarOpen,
            })}
          >
            <Container
              className={classes.container}
              style={{
                paddingTop: appBarHeight,
                maxWidth: 4600,
                paddingLeft: 0,
                paddingRight: 0,
                paddingBottom: 0,
              }}
            >
              {dashboardState.showInsightsDashboard && (
                <OverviewPage
                  search={search}
                  isUserAllowed={isUserAllowed}
                  dashboardLayoutView={overviewDashboardLayoutView}
                  setDashboardLayoutView={setOverviewDashboardLayoutView}
                  listings={listings}
                  otaListings={otaListings}
                  multipleMarkersCallLoading={multipleMarkersCallLoading}
                  multipleOTAMarkersCallLoading={multipleOTAMarkersCallLoading}
                  titleCase={titleCase}
                  visibleListings={extractVisibleListings(listings)}
                  visibleOTAListings={extractVisibleOTAListings(otaListings)}
                  dynamicVisibleListings={dynamicVisibleListings}
                  setDynamicVisibleListings={setDynamicVisibleListings}
                  staticListings={extractStaticListings(listings)}
                  sample={sample}
                  dashboardState={dashboardState}
                  openAlert={openAlert}
                  setOpenAlert={setOpenAlert}
                  setOTAListings={setOTAListings}
                  matches={matches}
                  filterApplied={filterApplied}
                  setListings={setListings}
                  dynamicTrigger={dynamicTrigger}
                  setDynamicTrigger={setDynamicTrigger}
                  subscriptionModalOpen={subscriptionModalOpen}
                  setSubscriptionModalOpen={setSubscriptionModalOpen}
                  overviewDashboardMode={overviewDashboardMode}
                  currency={currency}
                  user={user}
                  showSearchCreditDeductWarningModal={showSearchCreditDeductWarningModal}
                  setShowSearchCreditDeductWarningModal={setShowSearchCreditDeductWarningModal}
                  isLoggedIn={isLoggedIn}
                  searchLimitAlertBox={searchLimitAlertBox}
                  setSearchLimitAlertBox={setSearchLimitAlertBox}
                  handleOnSampleClick={handleOnSampleClick}
                  getMarkers={getMarkers}
                  bookingData={bookingData}
                  multipleBookingMarkersCallLoading={multipleBookingMarkersCallLoading}
                  isHighlight={isHighlight}
                  highlightElement={highlightElement}
                  removeHighlightElement={removeHighlightElement}
                  filterVal={filterVal}
                  filterActive={filterActive}
                  isEmailVerifyModalOpen={isEmailVerifyModalOpen}
                  isUserVerified={isUserVerified}
                  setIsEmailVerifyModalOpen={setIsEmailVerifyModalOpen}
                  rateLimitAlert={rateLimitAlert}
                  setRateLimitAlert={setRateLimitAlert}
                  revPotentialRangeInvestment={revPotentialRangeInvestment}
                  getHighLowRevPotentialInvestment={getHighLowRevPotentialInvestment}
                  bounds={boundsMapInsights}
                  setBounds={setBoundsMapInsights}
                  OnBoardingSearchBox={OnBoardingSearchBox}
                  setIndividualRevenuePotentialRange={setIndividualRevenuePotentialRange}
                  applyFilter={applyFilter}
                  setFilterActive={setFilterActive}
                  filteredListings={filteredListings}
                  filteredOTAListings={filteredOTAListings}
                  setIsTailoredRegionOpen={setIsTailoredRegionOpen}
                  updateShowAppTour={updateShowAppTour}
                  showAppTour={showAppTour}
                  toggleDrawer={toggleDrawer}
                  sidebarOpen={sidebarOpen}
                  setFilterVal={setFilterVal}
                  graph={graph}
                  cachedMarketData={cachedMarketData}
                  setLoginModalOpen={setLoginModalOpen}
                  filteredListingsDashboard={filteredListingsDashboard}
                  topPerformingMarketOnFocus={topPerformingMarketOnFocus}
                  setTopPerformingMarketOnFocus={setTopPerformingMarketOnFocus}
                />
              )}

              {dashboardState.showEstimatorDashboard && (
                <EstimatorDashboard
                  setSubscriptionModalOpen={setSubscriptionModalOpen}
                  subscriptionModalOpen={subscriptionModalOpen}
                  user={user}
                  isAdmin={isAdmin}
                  isLoggedIn={isLoggedIn}
                  setLoginModalOpen={setLoginModalOpen}
                  getUserInfo={getUserInfo}
                  matches={matches}
                  currency={currency}
                  isUserVerified={isUserVerified}
                  setIsEmailVerifyModalOpen={setIsEmailVerifyModalOpen}
                  googleAutocompleteSessionToken={googleAutocompleteSessionToken}
                  getGoogleAutocompleteSessionToken={getGoogleAutocompleteSessionToken}
                  clearGoogleAutocompleteSessionToken={clearGoogleAutocompleteSessionToken}
                  googleAutocompleteSessionTokenTimeStamp={googleAutocompleteSessionTokenTimeStamp}
                  centerCoord={centerCoord}
                  setCenterCoord={setCenterCoord}
                  address={address}
                  setAddress={setAddress}
                />
              )}
              {dashboardState.showMyAnalysisDashboard && (
                <MyAnalysisDashboard user={user} getUserInfo={getUserInfo} matches={matches} />
              )}
              {dashboardState.showSettingsDashboard && (
                <SettingsDashboard user={user} getUserInfo={getUserInfo} />
              )}
              {dashboardState.showDynamicPricingDashboard && (
                <DynamicPricingDashboard matches={matches} />
              )}
            </Container>
          </main>
        </>
      )}

      {isTailoredRegionOpen &&
        (matches ? (
          <OnboardingModalMobile
            getTokenThunk={getTokenThunk}
            setIsTailoredRegionOpen={setIsTailoredRegionOpen}
            tailoredRegionMarkers={tailoredRegionMarkers}
            getTailoredRegionMarkers={getTailoredRegionMarkers}
          />
        ) : (
          <OnboardingModal
            getTokenThunk={getTokenThunk}
            setIsTailoredRegionOpen={setIsTailoredRegionOpen}
            tailoredRegionMarkers={tailoredRegionMarkers}
            getTailoredRegionMarkers={getTailoredRegionMarkers}
          />
        ))}

      {isEmailVerifyModalOpen && (
        <EmailVerifyModal
          open={isEmailVerifyModalOpen}
          setOpen={setIsEmailVerifyModalOpen}
          email={user.email}
        />
      )}

      {!matches && !isMobile && (
        <AppTour
          appTourStep={appTourStep}
          updateAppTourStep={updateAppTourStep}
          showAppTour={showAppTour}
          updateShowAppTour={updateShowAppTour}
        />
      )}
    </>
  );
};

MarketDashboard.propTypes = {
  getMarkers: PropTypes.func,
  getGraphData: PropTypes.func,
  getSearch: PropTypes.func,
  searchChange: PropTypes.func,
  getTokenThunk: PropTypes.func,
  getUserInfo: PropTypes.func,
  getStatsPerADR: PropTypes.func,
  getBookingChanges: PropTypes.func,
};

export default MarketDashboard;
